import { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "react-oidc-context";
import { useQuery } from "@tanstack/react-query";
import { CalendarClock, Clock, Loader2, Mic, Plus, Save, Search, Video, X } from "@fg/icons";
import { connectParityApi, type Meeting } from "@/api/connectApi";
import { useAccessToken } from "@/auth/useAccessToken";
import { useCreateMeeting, useUpdateMeeting } from "@/api/hooks";
import { cn } from "@/lib/utils";
import { DEFAULT_TIME_ZONE, formatDay, formatTime, offsetLabel, zonedParts, zonedToInstant } from "@/lib/timezone";
import { useMailCalendarSettings } from "./meeting/mailCalendarApi";
import { SchedulingAssistant } from "./meeting/SchedulingAssistant";

interface Attendee {
    email: string;
    displayName: string | null;
    /** true when typed as a raw email rather than picked from the directory. */
    external: boolean;
}

interface Props {
    /** Pre-filled start time (from a clicked calendar slot) or null for "now-ish". */
    initialStart: Date | null;
    /** When set, the dialog edits this meeting (host only) instead of creating one. */
    meeting?: Meeting | null;
    onClose: () => void;
    /** Fires after a successful create so the caller can refresh + optionally join. */
    onCreated?: (meetingId: string, subject: string) => void;
    /** Fires after a successful edit. */
    onSaved?: () => void;
    /**
     * Close when the backdrop is clicked. On when the form was opened by
     * clicking a calendar slot (a quick, casual action); off when it was
     * opened from a button, so a stray click outside can't throw away what
     * you've typed — then only X / Cancel close it.
     */
    dismissOnBackdrop?: boolean;
}

const AUDIO_PREFIX = "Audio: ";

/**
 * Teams-style meeting scheduler — create a meeting, or edit one you host.
 * Title, date, start, duration, video/audio, agenda and attendees (org
 * directory autosuggest plus free-text external emails). Editing re-sends the
 * invitation with a higher SEQUENCE, so the entry moves in every attendee's
 * calendar; people added while editing get their own invitation.
 * <p>
 * Date and time are the organiser's calendar time zone (shared calendar
 * settings, default Asia/Kolkata). With attendees, the scheduling assistant
 * shows everyone's free/busy for the day and suggests free slots.
 */
export function MeetingScheduler({ initialStart, meeting, onClose, onCreated, onSaved, dismissOnBackdrop = true }: Props) {
    const token = useAccessToken();
    const auth = useAuth();
    const createMeeting = useCreateMeeting();
    const updateMeeting = useUpdateMeeting();
    const settings = useMailCalendarSettings();
    const tz = settings.data?.timeZone ?? DEFAULT_TIME_ZONE;
    const editing = Boolean(meeting);

    const start0 = useMemo(
        () => (meeting ? new Date(meeting.scheduledStart) : initialStart ?? nextHalfHour()),
        [meeting, initialStart]
    );
    const initialDuration = useMemo(() => {
        if (!meeting?.scheduledEnd) return 30;
        const mins = Math.round((new Date(meeting.scheduledEnd).getTime() - new Date(meeting.scheduledStart).getTime()) / 60_000);
        return mins > 0 ? mins : 30;
    }, [meeting]);

    const [title, setTitle] = useState(() =>
        meeting ? meeting.subject.replace(new RegExp(`^${AUDIO_PREFIX}`), "") : "");
    const [kind, setKind] = useState<"video" | "audio">(() =>
        meeting?.subject.startsWith(AUDIO_PREFIX) ? "audio" : "video");
    // The start is an instant; the date/time inputs show it in the organiser's zone.
    const [startsAt, setStartsAt] = useState<Date>(start0);
    const [durationMin, setDurationMin] = useState(initialDuration);
    const [description, setDescription] = useState(meeting?.description ?? "");
    const [attendees, setAttendees] = useState<Attendee[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    const titleRef = useRef<HTMLInputElement>(null);
    useEffect(() => { titleRef.current?.focus(); }, []);

    const parts = zonedParts(startsAt, tz);
    const dateValue = `${parts.year}-${pad(parts.month)}-${pad(parts.day)}`;
    const timeValue = `${pad(parts.hour)}:${pad(parts.minute)}`;
    const setDate = (v: string) => {
        const [y, m, d] = v.split("-").map(Number);
        if (!y || !m || !d) return;
        setStartsAt(zonedToInstant(y, m, d, parts.hour, parts.minute, tz));
    };
    const setTime = (v: string) => {
        const [h, mi] = v.split(":").map(Number);
        if (h === undefined || mi === undefined || Number.isNaN(h) || Number.isNaN(mi)) return;
        setStartsAt(zonedToInstant(parts.year, parts.month, parts.day, h, mi, tz));
    };

    const endsAt = useMemo(() => new Date(startsAt.getTime() + durationMin * 60_000), [startsAt, durationMin]);
    const durations = useMemo(
        () => Array.from(new Set([15, 30, 45, 60, 90, 120, durationMin])).sort((a, b) => a - b),
        [durationMin]
    );

    // Editing: everyone already invited belongs in the assistant too.
    const invited = useQuery({
        queryKey: ["meeting-attendees", meeting?.id, token],
        queryFn: () => connectParityApi.meetingInvitees(meeting!.id, token),
        enabled: Boolean(meeting && token)
    });
    const myEmail = (auth.user?.profile.email ?? "").toLowerCase();
    const assistantPeople = useMemo(() => {
        const list = [
            ...(invited.data ?? []).filter((a) => a.response !== "Declined").map((a) => ({ email: a.email, displayName: a.displayName })),
            ...attendees.map((a) => ({ email: a.email, displayName: a.displayName }))
        ];
        return list.filter((p, i) => list.findIndex((x) => x.email.toLowerCase() === p.email.toLowerCase()) === i);
    }, [invited.data, attendees]);
    const showAssistant = assistantPeople.length > 0 && Boolean(myEmail);

    const addAttendee = (a: Attendee) => {
        setAttendees((prev) =>
            prev.some((x) => x.email.toLowerCase() === a.email.toLowerCase()) ? prev : [...prev, a]);
    };
    const removeAttendee = (email: string) =>
        setAttendees((prev) => prev.filter((x) => x.email !== email));

    const submit = async () => {
        const plain = title.trim() || "Untitled meeting";
        // The backend infers audio/video from a subject prefix, so keep the
        // user's title but stamp the kind for the invite payload.
        const subject = kind === "audio" ? `${AUDIO_PREFIX}${plain}` : plain;
        setError(null);

        if (meeting) {
            setSaving(true);
            try {
                await updateMeeting.mutateAsync({
                    id: meeting.id,
                    subject,
                    scheduledStart: startsAt.toISOString(),
                    scheduledEnd: endsAt.toISOString(),
                    description: description.trim()
                });
                if (attendees.length > 0) {
                    await connectParityApi.inviteToMeeting(meeting.id, attendees.map((a) => a.email), token);
                }
                onSaved?.();
                onClose();
            } catch (e) {
                setError(e instanceof Error ? e.message : "Could not save the meeting.");
            } finally {
                setSaving(false);
            }
            return;
        }

        createMeeting.mutate(
            {
                subject,
                scheduledStart: startsAt.toISOString(),
                scheduledEnd: endsAt.toISOString(),
                invitees: attendees.map((a) => a.email),
                // Collected before but never sent — the agenda was lost.
                description: description.trim() || undefined
            },
            {
                onSuccess: (m) => { onCreated?.(m.id, plain); onClose(); },
                onError: (e) => setError(e instanceof Error ? e.message : "Could not create the meeting.")
            }
        );
    };

    const busy = saving || createMeeting.isPending;

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-label={editing ? "Edit meeting" : "Schedule a meeting"}
            className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 p-2 sm:p-4"
            onClick={(e) => { if (dismissOnBackdrop && e.target === e.currentTarget) onClose(); }}
            onKeyDown={(e) => { if (e.key === "Escape") { e.stopPropagation(); onClose(); } }}
        >
            <div className={cn(
                "flex max-h-[94vh] w-full flex-col overflow-hidden rounded-xl border bg-card shadow-2xl",
                showAssistant ? "max-w-4xl" : "max-w-lg"
            )}>
                <header className="flex items-center gap-2 border-b px-5 py-3">
                    <CalendarClock className="h-5 w-5 text-primary" />
                    <h2 className="text-base font-semibold">{editing ? "Edit meeting" : "New meeting"}</h2>
                    <button
                        type="button"
                        onClick={onClose}
                        className="ml-auto flex h-8 w-8 items-center justify-center rounded-md hover:bg-accent"
                        aria-label="Close"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </header>

                <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">
                    <div>
                        <label htmlFor="meeting-title" className="mb-1 block text-xs font-medium text-muted-foreground">Title</label>
                        <input
                            id="meeting-title"
                            ref={titleRef}
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void submit(); } }}
                            placeholder="Add a title"
                            className="h-10 w-full rounded-md border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-primary"
                        />
                    </div>

                    <div className="flex gap-2">
                        <TypeButton active={kind === "video"} onClick={() => setKind("video")} icon={<Video className="h-4 w-4" />} label="Video" />
                        <TypeButton active={kind === "audio"} onClick={() => setKind("audio")} icon={<Mic className="h-4 w-4" />} label="Audio" />
                    </div>

                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                        <div>
                            <label htmlFor="meeting-date" className="mb-1 block text-xs font-medium text-muted-foreground">Date</label>
                            <input
                                id="meeting-date"
                                type="date"
                                value={dateValue}
                                onChange={(e) => setDate(e.target.value)}
                                className="h-10 w-full rounded-md border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-primary"
                            />
                        </div>
                        <div>
                            <label htmlFor="meeting-start" className="mb-1 block text-xs font-medium text-muted-foreground">Start</label>
                            <input
                                id="meeting-start"
                                type="time"
                                value={timeValue}
                                onChange={(e) => setTime(e.target.value)}
                                className="h-10 w-full rounded-md border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-primary"
                            />
                        </div>
                        <div className="col-span-2 sm:col-span-1">
                            <label htmlFor="meeting-duration" className="mb-1 block text-xs font-medium text-muted-foreground">Duration</label>
                            <select
                                id="meeting-duration"
                                value={durationMin}
                                onChange={(e) => setDurationMin(Number(e.target.value))}
                                className="h-10 w-full rounded-md border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-primary"
                            >
                                {durations.map((m) => (
                                    <option key={m} value={m}>{formatDuration(m)}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <p className="-mt-2 flex flex-wrap items-center gap-1 text-[11px] text-muted-foreground">
                        <Clock className="h-3 w-3" aria-hidden />
                        {formatDay(startsAt, tz)} · {formatTime(startsAt, tz)} – {formatTime(endsAt, tz)} · {tz} ({offsetLabel(tz, startsAt)})
                    </p>

                    <div>
                        <span className="mb-1 block text-xs font-medium text-muted-foreground">
                            {editing ? "Add people" : "Attendees"} {attendees.length > 0 ? `(${attendees.length})` : ""}
                        </span>
                        {attendees.length > 0 ? (
                            <div className="mb-2 flex flex-wrap gap-1.5">
                                {attendees.map((a) => (
                                    <span
                                        key={a.email}
                                        className={cn(
                                            "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs",
                                            a.external ? "bg-amber-500/15 text-amber-700 dark:text-amber-300" : "bg-primary/10 text-primary"
                                        )}
                                        title={a.external ? "External guest" : a.email}
                                    >
                                        {a.displayName ?? a.email}
                                        <button type="button" onClick={() => removeAttendee(a.email)} aria-label={`Remove ${a.email}`}>
                                            <X className="h-3 w-3" />
                                        </button>
                                    </span>
                                ))}
                            </div>
                        ) : null}
                        <AttendeePicker token={token} onAdd={addAttendee} />
                        <p className="mt-1 text-[11px] text-muted-foreground">
                            {editing
                                ? "Everyone already invited gets the updated invitation. People you add here get a new one."
                                : "Search colleagues, or type any email to invite someone outside the org."}
                        </p>
                    </div>

                    {showAssistant ? (
                        <SchedulingAssistant
                            token={token}
                            organizer={{ email: myEmail, displayName: null }}
                            attendees={assistantPeople}
                            start={startsAt}
                            durationMin={durationMin}
                            timeZone={tz}
                            workDays={settings.data?.workDays ?? [1, 2, 3, 4, 5]}
                            workStart={settings.data?.workStart ?? "09:00"}
                            workEnd={settings.data?.workEnd ?? "18:00"}
                            onPick={setStartsAt}
                        />
                    ) : null}

                    <div>
                        <label htmlFor="meeting-agenda" className="mb-1 block text-xs font-medium text-muted-foreground">Agenda (optional)</label>
                        <textarea
                            id="meeting-agenda"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows={3}
                            placeholder="What's this meeting about?"
                            className="w-full resize-none rounded-md border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary"
                        />
                    </div>

                    {error ? <p className="text-xs text-destructive">{error}</p> : null}
                </div>

                <footer className="flex items-center justify-end gap-2 border-t px-5 py-3">
                    <button type="button" onClick={onClose} className="h-9 rounded-md border px-4 text-sm hover:bg-accent">
                        {editing ? "Discard" : "Cancel"}
                    </button>
                    <button
                        type="button"
                        onClick={() => void submit()}
                        disabled={busy}
                        className="flex h-9 items-center gap-1.5 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground disabled:opacity-50"
                    >
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? <Save className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                        {editing ? "Save and send update" : "Schedule"}
                    </button>
                </footer>
            </div>
        </div>
    );
}

function TypeButton({ active, onClick, icon, label }: { active: boolean; onClick: () => void; icon: React.ReactNode; label: string }) {
    return (
        <button
            type="button"
            onClick={onClick}
            aria-pressed={active}
            className={cn(
                "flex flex-1 items-center justify-center gap-1.5 rounded-md border py-2 text-sm font-medium",
                active ? "border-primary bg-primary/10 text-primary" : "hover:bg-accent"
            )}
        >
            {icon} {label}
        </button>
    );
}

function AttendeePicker({ token, onAdd }: { token: string | undefined; onAdd: (a: Attendee) => void }) {
    const [q, setQ] = useState("");
    const [results, setResults] = useState<Array<{ userId: string; email: string; displayName: string | null }>>([]);
    const [searching, setSearching] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const needle = q.trim();
        if (needle.length < 2 || !token) { setResults([]); return; }
        let cancelled = false;
        setSearching(true);
        const handle = setTimeout(async () => {
            try {
                const hits = await connectParityApi.searchDirectory(needle, token);
                if (!cancelled) { setResults(hits); setOpen(true); }
            } catch { if (!cancelled) setResults([]); }
            finally { if (!cancelled) setSearching(false); }
        }, 250);
        return () => { cancelled = true; clearTimeout(handle); };
    }, [q, token]);

    const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(q.trim());

    const commitExternal = () => {
        if (!isEmail) return;
        onAdd({ email: q.trim().toLowerCase(), displayName: null, external: true });
        setQ(""); setResults([]); setOpen(false);
    };

    return (
        <div className="relative">
            <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <input
                    value={q}
                    onChange={(e) => { setQ(e.target.value); setOpen(true); }}
                    onKeyDown={(e) => { if (e.key === "Enter" && isEmail) { e.preventDefault(); commitExternal(); } }}
                    onBlur={() => setTimeout(() => setOpen(false), 150)}
                    placeholder="Name or email"
                    aria-label="Add attendees"
                    className="h-10 w-full rounded-md border bg-background pl-9 pr-3 text-sm outline-none focus:ring-2 focus:ring-primary"
                />
            </div>
            {open && (q.trim().length >= 2) ? (
                <div className="absolute left-0 right-0 top-11 z-10 overflow-hidden rounded-md border bg-popover shadow-lg">
                    {results.length > 0 ? (
                        <ul className="max-h-48 overflow-auto">
                            {results.map((u) => (
                                <li key={u.userId}>
                                    <button
                                        type="button"
                                        onMouseDown={(e) => {
                                            e.preventDefault();
                                            onAdd({ email: u.email, displayName: u.displayName, external: false });
                                            setQ(""); setResults([]); setOpen(false);
                                        }}
                                        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-accent"
                                    >
                                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-[10px] font-semibold">
                                            {(u.displayName ?? u.email).charAt(0).toUpperCase()}
                                        </span>
                                        <span className="min-w-0 flex-1">
                                            <span className="block truncate">{u.displayName ?? u.email}</span>
                                            <span className="block truncate text-[10px] text-muted-foreground">{u.email}</span>
                                        </span>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    ) : isEmail ? (
                        <button
                            type="button"
                            onMouseDown={(e) => { e.preventDefault(); commitExternal(); }}
                            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-accent"
                        >
                            <Plus className="h-4 w-4" /> Invite external guest <span className="font-medium">{q.trim()}</span>
                        </button>
                    ) : (
                        <p className="px-3 py-2 text-xs text-muted-foreground">
                            {searching ? "Searching…" : "No matches — type a full email to invite an external guest."}
                        </p>
                    )}
                </div>
            ) : null}
        </div>
    );
}

// ── date helpers ─────────────────────────────────────────────────────────────
function nextHalfHour(): Date {
    const d = new Date();
    d.setMinutes(d.getMinutes() < 30 ? 30 : 60, 0, 0);
    return d;
}
function pad(n: number): string { return n < 10 ? `0${n}` : String(n); }
function formatDuration(m: number): string {
    if (m < 60) return `${m} min`;
    const h = Math.floor(m / 60);
    const rest = m % 60;
    return rest === 0 ? `${h} hr${h > 1 ? "s" : ""}` : `${h} hr ${rest} min`;
}
